// src/pages/CRM/Core/components/BulkServiceReminderDialog.js

import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Alert,
  Stack,
  Typography,
  CircularProgress,
  Chip,
  Box,
} from '@mui/material';
import axiosInstance from '../../../../../utils/axiosInstance';
import {
  ServiceReminderTypeLabels,
  RecurrenceTypeLabels,
} from '../crmEnums';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";

const emptyForm = {
  title: '',
  description: '',
  service_type_id: '',
  service_type_name: '',
  service_date: '',
  reminder_type: 'OTHER',
  recurrence_type: 'ONE_TIME',
  recurrence_end_date: '',
  notify_via_sms: false,
  notify_via_email: true,
  sms_message: '',
  email_subject: '',
  email_body: '',
  remind_before_days: 1,
  remind_on_day: true,
};

export default function BulkServiceReminderDialog({ open, onClose, selectedContacts, chatbotId }) {
  const [form, setForm] = useState({ ...emptyForm });
  const [serviceTypes, setServiceTypes] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);

  const loadServiceTypes = useCallback(async () => {
    try {
      const resp = await axiosInstance.get('/api/crm/service-types');
      if (resp.data.success) {
        setServiceTypes(resp.data.data || []);
      }
    } catch (err) {
      console.error('Failed to load service types:', err);
    }
  }, []);

  useEffect(() => {
    if (open) {
      setForm({ ...emptyForm });
      setError('');
      setResult(null);
      loadServiceTypes();
    }
  }, [open, loadServiceTypes]);

  const handleServiceTypeChange = (e) => {
    const typeId = e.target.value;
    const type = serviceTypes.find((t) => t.id === typeId);
    setForm((prev) => ({
      ...prev,
      service_type_id: typeId,
      service_type_name: type ? type.name : prev.service_type_name,
      title: type ? type.name : prev.title,
    }));
  };

  const handleSave = async () => {
    if (!form.title || !form.service_date) {
      setError('Title and service date are required');
      return;
    }

    setSaving(true);
    setError('');
    setResult(null);
    try {
      const payload = {
        ...form,
        contact_ids: selectedContacts.map((c) => c.id),
        chatbot_id: chatbotId || undefined,
        service_type_id: form.service_type_id || undefined,
      };

      const resp = await axiosInstance.post('/api/crm/service-reminders/bulk', payload);
      if (resp.data.success) {
        setResult(resp.data);
      } else {
        setError(resp.data.message || 'Failed to create reminders');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create reminders');
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setForm({ ...emptyForm });
    setError('');
    setResult(null);
    onClose(!!result);
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth
      PaperProps={{ sx: { borderRadius: '14px' } }}
    >
      <DialogTitle sx={{ fontFamily, fontWeight: 700, fontSize: '1rem' }}>
        Bulk Service Reminder
      </DialogTitle>
      <DialogContent>
        {/* Selected contacts summary */}
        <Box sx={{ mb: 2, p: 1.5, bgcolor: '#f1f5f9', borderRadius: '8px' }}>
          <Typography sx={{ fontFamily, fontSize: '0.8rem', fontWeight: 600, mb: 0.5 }}>
            {selectedContacts.length} contact{selectedContacts.length !== 1 ? 's' : ''} selected
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {selectedContacts.slice(0, 10).map((c) => (
              <Chip
                key={c.id}
                label={[c.first_name, c.last_name].filter(Boolean).join(' ') || `Contact #${c.id}`}
                size="small"
                sx={{ fontFamily, fontSize: '0.7rem', height: 22 }}
              />
            ))}
            {selectedContacts.length > 10 && (
              <Chip
                label={`+${selectedContacts.length - 10} more`}
                size="small"
                variant="outlined"
                sx={{ fontFamily, fontSize: '0.7rem', height: 22 }}
              />
            )}
          </Box>
        </Box>

        {result ? (
          <Alert severity="success" sx={{ fontFamily }}>
            {result.message}
            {result.data?.skipped?.length > 0 && (
              <Typography sx={{ fontFamily, fontSize: '0.75rem', mt: 1 }}>
                Skipped {result.data.skipped.length} contact(s)
              </Typography>
            )}
          </Alert>
        ) : (
          <>
            {error && <Alert severity="error" sx={{ mb: 2, fontFamily }}>{error}</Alert>}

            <Stack spacing={2} sx={{ mt: 1 }}>
              <FormControl fullWidth size="small">
                <InputLabel sx={{ fontFamily }}>Service Type</InputLabel>
                <Select
                  value={form.service_type_id}
                  onChange={handleServiceTypeChange}
                  label="Service Type"
                  sx={{ fontFamily }}
                >
                  <MenuItem value=""><em>Custom</em></MenuItem>
                  {serviceTypes.map((t) => (
                    <MenuItem key={t.id} value={t.id} sx={{ fontFamily }}>{t.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              {!form.service_type_id && (
                <TextField
                  label="Custom Service Type"
                  value={form.service_type_name}
                  onChange={(e) => setForm((p) => ({ ...p, service_type_name: e.target.value }))}
                  size="small"
                  fullWidth
                  InputProps={{ sx: { fontFamily } }}
                  InputLabelProps={{ sx: { fontFamily } }}
                />
              )}

              <TextField
                label="Title"
                value={form.title}
                onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))}
                size="small"
                fullWidth
                required
                InputProps={{ sx: { fontFamily } }}
                InputLabelProps={{ sx: { fontFamily } }}
              />

              <TextField
                label="Description"
                value={form.description}
                onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
                size="small"
                fullWidth
                multiline
                rows={2}
                InputProps={{ sx: { fontFamily } }}
                InputLabelProps={{ sx: { fontFamily } }}
              />

              <TextField
                label="Service Date"
                type="datetime-local"
                value={form.service_date}
                onChange={(e) => setForm((p) => ({ ...p, service_date: e.target.value }))}
                size="small"
                fullWidth
                required
                InputLabelProps={{ shrink: true, sx: { fontFamily } }}
                InputProps={{ sx: { fontFamily } }}
              />

              <FormControl fullWidth size="small">
                <InputLabel sx={{ fontFamily }}>Reminder Type</InputLabel>
                <Select
                  value={form.reminder_type}
                  onChange={(e) => setForm((p) => ({ ...p, reminder_type: e.target.value }))}
                  label="Reminder Type"
                  sx={{ fontFamily }}
                >
                  {Object.entries(ServiceReminderTypeLabels).map(([key, label]) => (
                    <MenuItem key={key} value={key} sx={{ fontFamily }}>{label}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth size="small">
                <InputLabel sx={{ fontFamily }}>Frequency</InputLabel>
                <Select
                  value={form.recurrence_type}
                  onChange={(e) => setForm((p) => ({ ...p, recurrence_type: e.target.value }))}
                  label="Frequency"
                  sx={{ fontFamily }}
                >
                  {Object.entries(RecurrenceTypeLabels).map(([key, label]) => (
                    <MenuItem key={key} value={key} sx={{ fontFamily }}>{label}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              {form.recurrence_type !== 'ONE_TIME' && (
                <TextField
                  label="Recurrence End Date (optional)"
                  type="datetime-local"
                  value={form.recurrence_end_date}
                  onChange={(e) => setForm((p) => ({ ...p, recurrence_end_date: e.target.value }))}
                  size="small"
                  fullWidth
                  InputLabelProps={{ shrink: true, sx: { fontFamily } }}
                  InputProps={{ sx: { fontFamily } }}
                  helperText="Leave empty for indefinite recurrence"
                />
              )}

              <Box>
                <Typography sx={{ fontFamily, fontSize: '0.8rem', fontWeight: 600, mb: 0.5 }}>
                  Notification Channels
                </Typography>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={form.notify_via_email}
                      onChange={(e) => setForm((p) => ({ ...p, notify_via_email: e.target.checked }))}
                      size="small"
                    />
                  }
                  label={<Typography sx={{ fontFamily, fontSize: '0.8rem' }}>Email</Typography>}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={form.notify_via_sms}
                      onChange={(e) => setForm((p) => ({ ...p, notify_via_sms: e.target.checked }))}
                      size="small"
                    />
                  }
                  label={<Typography sx={{ fontFamily, fontSize: '0.8rem' }}>SMS</Typography>}
                />
              </Box>

              {form.notify_via_email && (
                <>
                  <TextField
                    label="Custom Email Subject (optional)"
                    value={form.email_subject}
                    onChange={(e) => setForm((p) => ({ ...p, email_subject: e.target.value }))}
                    size="small"
                    fullWidth
                    InputProps={{ sx: { fontFamily } }}
                    InputLabelProps={{ sx: { fontFamily } }}
                  />
                  <TextField
                    label="Custom Email Body (optional)"
                    value={form.email_body}
                    onChange={(e) => setForm((p) => ({ ...p, email_body: e.target.value }))}
                    size="small"
                    fullWidth
                    multiline
                    rows={3}
                    InputProps={{ sx: { fontFamily } }}
                    InputLabelProps={{ sx: { fontFamily } }}
                  />
                </>
              )}

              {form.notify_via_sms && (
                <TextField
                  label="Custom SMS Message (optional)"
                  value={form.sms_message}
                  onChange={(e) => setForm((p) => ({ ...p, sms_message: e.target.value }))}
                  size="small"
                  fullWidth
                  multiline
                  rows={2}
                  InputProps={{ sx: { fontFamily } }}
                  InputLabelProps={{ sx: { fontFamily } }}
                />
              )}

              <Box>
                <Typography sx={{ fontFamily, fontSize: '0.8rem', fontWeight: 600, mb: 0.5 }}>
                  Reminder Timing
                </Typography>
                {form.recurrence_type === 'DAILY' ? (
                  <Typography sx={{ fontFamily, fontSize: '0.75rem', color: '#64748b' }}>
                    Daily reminders are sent on each occurrence day automatically.
                  </Typography>
                ) : (
                  <Stack direction="row" spacing={2} alignItems="center">
                    <TextField
                      label="Days before"
                      type="number"
                      value={form.remind_before_days}
                      onChange={(e) => {
                        let val = parseInt(e.target.value) || 0;
                        const max = form.recurrence_type === 'WEEKLY' ? 6 : form.recurrence_type === 'MONTHLY' ? 14 : 365;
                        if (val > max) val = max;
                        if (val < 0) val = 0;
                        setForm((p) => ({ ...p, remind_before_days: val }));
                      }}
                      size="small"
                      sx={{ width: 120 }}
                      InputProps={{
                        sx: { fontFamily },
                        inputProps: {
                          min: 0,
                          max: form.recurrence_type === 'WEEKLY' ? 6 : form.recurrence_type === 'MONTHLY' ? 14 : 365,
                        },
                      }}
                      InputLabelProps={{ sx: { fontFamily } }}
                      helperText={
                        form.recurrence_type === 'WEEKLY' ? 'Max 6 days' :
                        form.recurrence_type === 'MONTHLY' ? 'Max 14 days' : ''
                      }
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={form.remind_on_day}
                          onChange={(e) => setForm((p) => ({ ...p, remind_on_day: e.target.checked }))}
                          size="small"
                        />
                      }
                      label={<Typography sx={{ fontFamily, fontSize: '0.8rem' }}>Also remind on the day</Typography>}
                    />
                  </Stack>
                )}
              </Box>
            </Stack>
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} sx={{ fontFamily, textTransform: 'none' }}>
          {result ? 'Close' : 'Cancel'}
        </Button>
        {!result && (
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving}
            sx={{ fontFamily, textTransform: 'none', fontWeight: 600, borderRadius: '8px' }}
          >
            {saving ? <CircularProgress size={20} /> : `Create for ${selectedContacts.length} Contact${selectedContacts.length !== 1 ? 's' : ''}`}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
